import {
  SPORT_DASHBOARD_PATHS,
  SPORT_DASHBOARD_ROUTES,
} from "@/routes/match_hub/constants";

/** Per-sport discover & organize match pages (UI, routes, API). */
export const ORGANIZE_MATCH_CONFIG = Object.freeze({
  football: {
    slug: "football",
    routeName: "FootballOrganizeMatch",
    dashboardRoute: SPORT_DASHBOARD_ROUTES.football,
    dashboardPath: SPORT_DASHBOARD_PATHS.football,
    challengeRoute: "ChallengeTeam",
    organizePath: "/ipromiseafrica/matchup/football/organize-match",
    discoverApi: "/api/teams/discover",
    myTeamApi: "/api/teams/my-teams",
    createMatchApiTemplate: "/api/teams/{teamId}/matches/create",
    discoverEnabled: true,
    challengeEnabled: true,
    createMatchEnabled: true,
    kicker: "Football · Organize Match",
    title: "Discover Football Teams",
    subtitle: "Find 11-a-side squads, filter by location and level, then challenge a rival for your next fixture.",
    icon: "fa-solid fa-futbol",
    themeClass: "organize-match--football",
    matchStatIcon: "fa-solid fa-futbol",
    venueLabel: "Pitch / ground name",
    venuePlaceholder: "e.g. Nyayo National Stadium",
    challengeButtonLabel: "Challenge Team",
    durationOptions: [
      { value: "60", label: "60 minutes" },
      { value: "90", label: "90 minutes" },
      { value: "120", label: "120 minutes" },
    ],
    extraFilters: [],
    previewMessage: null,
  },
  basketball: {
    slug: "basketball",
    routeName: "BasketballOrganizeMatch",
    dashboardRoute: SPORT_DASHBOARD_ROUTES.basketball,
    dashboardPath: SPORT_DASHBOARD_PATHS.basketball,
    challengeRoute: "BasketballChallengeTeam",
    organizePath: "/ipromiseafrica/matchup/basketball/organize-match",
    discoverApi: "/api/teams/basketball/discover",
    myTeamApi: "/api/teams/basketball/my-team",
    createMatchApiTemplate: "/api/matches/basketball/matches/create/{opponentTeamId}",
    discoverEnabled: true,
    challengeEnabled: true,
    createMatchEnabled: true,
    kicker: "Basketball · Organize Match",
    title: "Discover Basketball Crews",
    subtitle:
      "Browse 5v5 and 3v3 squads by court type and skill. Results are basketball teams only.",
    icon: "fa-solid fa-basketball",
    themeClass: "organize-match--basketball",
    matchStatIcon: "fa-solid fa-basketball",
    venueLabel: "Court / gym name",
    venuePlaceholder: "e.g. Nyayo Indoor Arena",
    challengeButtonLabel: "Request Game",
    durationOptions: [
      { value: "40", label: "40 minutes (4×10)" },
      { value: "48", label: "48 minutes (4×12)" },
      { value: "60", label: "60 minutes (running clock)" },
    ],
    extraFilters: [
      {
        key: "game_format",
        label: "Game format",
        icon: "fa-solid fa-basketball",
        options: [
          { value: "", label: "All formats" },
          { value: "5v5", label: "5v5 full court" },
          { value: "3v3", label: "3v3 half court" },
          { value: "both", label: "5v5 & 3v3" },
        ],
      },
      {
        key: "court_type",
        label: "Court type",
        icon: "fa-solid fa-building",
        options: [
          { value: "", label: "All courts" },
          { value: "indoor", label: "Indoor" },
          { value: "outdoor", label: "Outdoor" },
          { value: "both", label: "Indoor & outdoor" },
        ],
      },
    ],
    previewMessage: null,
  },
  rugby: {
    slug: "rugby",
    routeName: "RugbyOrganizeMatch",
    dashboardRoute: SPORT_DASHBOARD_ROUTES.rugby,
    dashboardPath: SPORT_DASHBOARD_PATHS.rugby,
    challengeRoute: "RugbyChallengeTeam",
    organizePath: "/ipromiseafrica/matchup/rugby/organize-match",
    discoverApi: "/api/teams/rugby/discover",
    myTeamApi: "/api/teams/rugby/my-team",
    createMatchApiTemplate: null,
    discoverEnabled: true,
    challengeEnabled: true,
    createMatchEnabled: false,
    kicker: "Rugby · Organize Match",
    title: "Discover Rugby Clubs",
    subtitle:
      "Find XV, 7s, and 10s clubs by pitch and format. Results are rugby clubs only.",
    icon: "fa-solid fa-football",
    themeClass: "organize-match--rugby",
    matchStatIcon: "fa-solid fa-football",
    venueLabel: "Pitch / club ground",
    venuePlaceholder: "e.g. RFUEA Ground",
    challengeButtonLabel: "Challenge Club",
    durationOptions: [
      { value: "80", label: "80 minutes (2×40)" },
      { value: "70", label: "70 minutes (2×35)" },
      { value: "14", label: "14 minutes (7s)" },
    ],
    extraFilters: [
      {
        key: "game_format",
        label: "Format",
        icon: "fa-solid fa-people-group",
        options: [
          { value: "", label: "All formats" },
          { value: "15s", label: "15-a-side" },
          { value: "7s", label: "7-a-side" },
          { value: "10s", label: "10-a-side" },
        ],
      },
      {
        key: "pitch_surface",
        label: "Pitch surface",
        icon: "fa-solid fa-seedling",
        options: [
          { value: "", label: "All surfaces" },
          { value: "grass", label: "Natural grass" },
          { value: "artificial", label: "Artificial turf" },
        ],
      },
    ],
    previewMessage: null,
  },
  volleyball: {
    slug: "volleyball",
    routeName: "VolleyballOrganizeMatch",
    dashboardRoute: SPORT_DASHBOARD_ROUTES.volleyball,
    dashboardPath: SPORT_DASHBOARD_PATHS.volleyball,
    challengeRoute: "VolleyballChallengeTeam",
    organizePath: "/ipromiseafrica/matchup/volleyball/organize-match",
    discoverApi: "/api/teams/volleyball/discover",
    myTeamApi: "/api/teams/volleyball/my-team",
    createMatchApiTemplate: null,
    discoverEnabled: true,
    challengeEnabled: true,
    createMatchEnabled: false,
    kicker: "Volleyball · Organize Match",
    title: "Discover Volleyball Squads",
    subtitle:
      "Browse indoor, outdoor, and beach crews. Results are volleyball squads only.",
    icon: "fa-solid fa-volleyball",
    themeClass: "organize-match--volleyball",
    matchStatIcon: "fa-solid fa-volleyball",
    venueLabel: "Venue / court name",
    venuePlaceholder: "e.g. Kasarani Indoor Arena",
    challengeButtonLabel: "Request Match",
    durationOptions: [
      { value: "best_of_3", label: "Best of 3 sets" },
      { value: "best_of_5", label: "Best of 5 sets" },
      { value: "single", label: "Single set to 25" },
    ],
    extraFilters: [
      {
        key: "game_format",
        label: "Format",
        icon: "fa-solid fa-volleyball",
        options: [
          { value: "", label: "All formats" },
          { value: "6v6", label: "6v6 indoor" },
          { value: "5v5", label: "5v5 recreational" },
          { value: "beach", label: "Beach" },
        ],
      },
      {
        key: "court_type",
        label: "Venue type",
        icon: "fa-solid fa-umbrella-beach",
        options: [
          { value: "", label: "All venues" },
          { value: "indoor", label: "Indoor" },
          { value: "outdoor", label: "Outdoor" },
          { value: "beach", label: "Beach" },
        ],
      },
    ],
    previewMessage: null,
  },
  tennis: {
    slug: "tennis",
    routeName: "TennisOrganizeMatch",
    dashboardRoute: SPORT_DASHBOARD_ROUTES.tennis,
    dashboardPath: SPORT_DASHBOARD_PATHS.tennis,
    challengeRoute: "TennisChallengeTeam",
    organizePath: "/ipromiseafrica/matchup/tennis/organize-match",
    discoverApi: "/api/teams/tennis/discover",
    myTeamApi: "/api/teams/tennis/my-team",
    createMatchApiTemplate: null,
    discoverEnabled: true,
    challengeEnabled: true,
    createMatchEnabled: false,
    kicker: "Tennis · Organize Match",
    title: "Discover Tennis Teams",
    subtitle:
      "Find singles and doubles teams by court type and level. Results are tennis teams only.",
    icon: "fa-solid fa-table-tennis-paddle-ball",
    themeClass: "organize-match--tennis",
    matchStatIcon: "fa-solid fa-table-tennis-paddle-ball",
    venueLabel: "Court / club name",
    venuePlaceholder: "e.g. Nairobi Club",
    challengeButtonLabel: "Challenge Team",
    durationOptions: [
      { value: "60", label: "60 minutes" },
      { value: "90", label: "90 minutes" },
      { value: "120", label: "120 minutes" },
    ],
    extraFilters: [
      {
        key: "game_format",
        label: "Format",
        icon: "fa-solid fa-users",
        options: [
          { value: "", label: "All formats" },
          { value: "singles", label: "Singles" },
          { value: "doubles", label: "Doubles" },
          { value: "both", label: "Singles & doubles" },
        ],
      },
      {
        key: "court_type",
        label: "Court type",
        icon: "fa-solid fa-building",
        options: [
          { value: "", label: "All courts" },
          { value: "hard", label: "Hard court" },
          { value: "clay", label: "Clay" },
          { value: "grass", label: "Grass" },
          { value: "indoor", label: "Indoor" },
        ],
      },
    ],
    previewMessage: null,
  },
});

export const SPORT_ORGANIZE_MATCH_ROUTES = Object.freeze(
  Object.fromEntries(
    Object.entries(ORGANIZE_MATCH_CONFIG).map(([slug, cfg]) => [slug, cfg.routeName]),
  ),
);

export const SPORT_ORGANIZE_MATCH_PATHS = Object.freeze(
  Object.fromEntries(
    Object.entries(ORGANIZE_MATCH_CONFIG).map(([slug, cfg]) => [slug, cfg.organizePath]),
  ),
);

export function getOrganizeMatchConfig(sportSlug) {
  return ORGANIZE_MATCH_CONFIG[sportSlug] || ORGANIZE_MATCH_CONFIG.football;
}
